import { useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
	changeValue,
	refresh,
	getBase,
	getExplain,
	getSellingpoint,
	getDiagnosis,
	getInspect,
	getInspectSummary,
	getOption,
	getOptionStandard,
	getOptionStandardTruck,
	getOptionChoice,
	getOptionTuning,
	getAccidentSummary,
	getAccidentOpen,
	getExtendWarranty,
	getLeaseSuccession,
	getRent,
	getRentSuccession,
	getPhoto,
	getLoggedAuth,
	getProfile,
	getMochaDetail,
	getMochaRequest,
	getUsersFavorites,
	postUsersFavorite,
	cancelUsersFavorite,
	postLoanJudgement,
	getCheckFalseCar,
	getItems,
	getAcquisitionCost,
	getAddress,
	getEstimate,
	getEscrowCarExistence,
	postEncarpayLimitCheck,
	getEncarpayActivatedFinancial,
	postNewCarPriceAccessKey,
	getPartnerCenter,
	getAffiliationCompany,
	getEvBattery,
} from 'app/store/modules/cars';
import { getSimpleOptionAction, getSimpleOptionV2Action } from 'app/store/modules/carVerification';
import usePender from 'app/hook/common/usePender';

export default function useCarInfo() {
	const {
		isRefresh,
		detailFlags,
		base,
		photo,
		explain,
		sellingpoint,
		diagnosis,
		inspect,
		inspectSummary,
		option,
		optionStandard,
		optionStandardTruck,
		optionChoice,
		optionTuning,
		accidentSummary,
		accident,
		warranty,
		leaseSuccession,
		rent,
		rentSuccession,
		profile,
		mochaFaq,
		mochaRequest,
		usersFavorites,
		loanJudgement,
		loggedAuth,
		falseCar,
		items,
		acquisitionCost,
		address,
		estimate,
		encarpayLimitCheck,
		escrow,
		encarpayAcitvatedFinancial,
		inquiryOpen,
		newCarPriceAccessKey,
		partnerCenter,
		affiliationCompany,
		userSelectRentValue,
		evBattery,
	} = useSelector((state) => state.cars, shallowEqual);
	const { simpleReport, simpleReportV2 } = useSelector((state) => state.carVerification, shallowEqual);

	const dispatch = useDispatch();
	const { getSuccess } = usePender();

	const changeComm = useCallback((payload) => dispatch(changeValue(payload)), [dispatch]);

	const initRefresh = useCallback(() => dispatch(refresh()), [dispatch]);

	const getBaseInfo = useCallback((carId, auth) => dispatch(getBase(carId, auth)), [dispatch]);
	const isGetBaseInfoSuccess = getSuccess('cars/GET_BASE');

	const getExplainInfo = useCallback((carId, auth) => dispatch(getExplain(carId, auth)), [dispatch]);

	const getSellingpointInfo = useCallback((carId, auth) => dispatch(getSellingpoint(carId, auth)), [dispatch]);

	const getDiagnosisInfo = useCallback((carId, auth) => dispatch(getDiagnosis(carId, auth)), [dispatch]);

	const getInspectInfo = useCallback((carId, auth) => dispatch(getInspect(carId, auth)), [dispatch]);

	const getInspectSummaryInfo = useCallback((carId, auth) => dispatch(getInspectSummary(carId, auth)), [dispatch]);

	const getOptionInfo = useCallback((carId, auth) => dispatch(getOption(carId, auth)), [dispatch]);

	const getOptionChoiceInfo = useCallback((carId, auth) => dispatch(getOptionChoice(carId, auth)), [dispatch]);

	const getOptionStandardInfo = useCallback(() => dispatch(getOptionStandard()), [dispatch]);

	const getOptionStandardTruckInfo = useCallback(() => dispatch(getOptionStandardTruck()), [dispatch]);

	const getOptionTuningInfo = useCallback(() => dispatch(getOptionTuning()), [dispatch]);

	const getAccidentSummaryInfo = useCallback((carId, auth) => dispatch(getAccidentSummary(carId, auth)), [dispatch]);

	const getAccidentOpenInfo = useCallback((carId, carNo, auth) => dispatch(getAccidentOpen(carId, carNo, auth)), [dispatch]);

	const getWarrantyInfo = useCallback((carId, auth) => dispatch(getExtendWarranty(carId, auth)), [dispatch]);

	const getLeaseSuccessionInfo = useCallback((carId, auth) => dispatch(getLeaseSuccession(carId, auth)), [dispatch]);

	const getRentInfo = useCallback((carId, auth) => dispatch(getRent(carId, auth)), [dispatch]);

	const getRentSuccessionInfo = useCallback((carId, auth) => dispatch(getRentSuccession(carId, auth)), [dispatch]);

	const getProfileInfo = useCallback((userId, auth) => dispatch(getProfile(userId, auth)), [dispatch]);

	const getPhotoInfo = useCallback((carId, auth) => dispatch(getPhoto(carId, auth)), [dispatch]);

	const getMochaDetailInfo = useCallback((manufacturer, model, auth) => dispatch(getMochaDetail(manufacturer, model, auth)), [dispatch]);

	const getMochaRequestInfo = useCallback((manufacturer, model, auth) => dispatch(getMochaRequest(manufacturer, model, auth)), [dispatch]);

	const getUsersFavoritesInfo = useCallback((userId, auth) => dispatch(getUsersFavorites(userId, auth)), [dispatch]);

	const postUsersFavoriteInfo = useCallback((carId, userId, auth) => dispatch(postUsersFavorite(carId, userId, auth)), [dispatch]);

	const cancelUsersFavoriteInfo = useCallback((carId, userId, auth) => dispatch(cancelUsersFavorite(carId, userId, auth)), [dispatch]);

	const postLoanJudgementInfo = useCallback((carId, userId, auth) => dispatch(postLoanJudgement(carId, userId, auth)), [dispatch]);

	const getLoggedAuthInfo = useCallback((auth) => dispatch(getLoggedAuth(auth)), [dispatch]);

	const getCheckFalseCarInfo = useCallback((carId, auth) => dispatch(getCheckFalseCar(carId, auth)), [dispatch]);

	const getItemsInfo = useCallback((carId, auth) => dispatch(getItems(carId, auth)), [dispatch]);

	const getAcquisitionCostInfo = useCallback(
		(carId, purpose, price, otherLocation, auth) => dispatch(getAcquisitionCost(carId, purpose, price, otherLocation, auth)),
		[dispatch],
	);

	const getAddressInfo = useCallback((carId, auth) => dispatch(getAddress(carId, auth)), [dispatch]);

	const getEstimateInfo = useCallback(
		(carId, buildingCd, extendedWarrantyMonth, auth) => dispatch(getEstimate(carId, buildingCd, extendedWarrantyMonth, auth)),
		[dispatch],
	);

	const getEscrowCarExistenceInfo = useCallback((carId) => dispatch(getEscrowCarExistence(carId)), [dispatch]);

	const postEncarpayLimitCheckInfo = useCallback((hash) => dispatch(postEncarpayLimitCheck(hash)), [dispatch]);

	const getEncarPayActivatedFinancialInfo = useCallback((inflowType) => dispatch(getEncarpayActivatedFinancial(inflowType)), [dispatch]);

	const getReportSimpleOption = useCallback((options) => dispatch(getSimpleOptionAction(options)), [dispatch]);
	const getReportSimpleOptionV2 = useCallback((options) => dispatch(getSimpleOptionV2Action(options)), [dispatch]);

	const postNewCarPriceAccessKeyInfo = useCallback((host) => dispatch(postNewCarPriceAccessKey(host)), [dispatch]);

	const getPartnerCenterInfo = useCallback((manufacturerCd) => dispatch(getPartnerCenter(manufacturerCd)), [dispatch]);

	const getAffiliationCompanyInfo = useCallback((id) => dispatch(getAffiliationCompany(id)), [dispatch]);

	const getEvBatteryInfo = useCallback((carId, auth) => dispatch(getEvBattery(carId, auth)), [dispatch]);

	return {
		changeComm,
		isRefresh,
		initRefresh,
		getBaseInfo,
		isGetBaseInfoSuccess,
		getExplainInfo,
		getSellingpointInfo,
		getDiagnosisInfo,
		getOptionInfo,
		getOptionStandardInfo,
		getOptionStandardTruckInfo,
		getOptionChoiceInfo,
		getOptionTuningInfo,
		getAccidentSummaryInfo,
		getAccidentOpenInfo,
		getWarrantyInfo,
		getLeaseSuccessionInfo,
		getRentInfo,
		getRentSuccessionInfo,
		getInspectInfo,
		getInspectSummaryInfo,
		getPhotoInfo,
		getProfileInfo,
		getMochaDetailInfo,
		getMochaRequestInfo,
		getUsersFavoritesInfo,
		postUsersFavoriteInfo,
		cancelUsersFavoriteInfo,
		postLoanJudgementInfo,
		getLoggedAuthInfo,
		getCheckFalseCarInfo,
		getItemsInfo,
		getAcquisitionCostInfo,
		getAddressInfo,
		getEstimateInfo,
		getEscrowCarExistenceInfo,
		postEncarpayLimitCheckInfo,
		getEncarPayActivatedFinancialInfo,
		getReportSimpleOption,
		getReportSimpleOptionV2,
		postNewCarPriceAccessKeyInfo,
		getPartnerCenterInfo,
		getAffiliationCompanyInfo,
		detailFlags,
		base,
		photo,
		explain,
		sellingpoint,
		diagnosis,
		inspect,
		inspectSummary,
		option,
		optionStandard,
		optionStandardTruck,
		optionChoice,
		optionTuning,
		accidentSummary,
		accident,
		warranty,
		leaseSuccession,
		rent,
		rentSuccession,
		profile,
		mochaFaq,
		mochaRequest,
		usersFavorites,
		loanJudgement,
		loggedAuth,
		falseCar,
		items,
		acquisitionCost,
		address,
		estimate,
		encarpayLimitCheck,
		encarpayAcitvatedFinancial,
		escrow,
		inquiryOpen,
		simpleReport,
		simpleReportV2,
		newCarPriceAccessKey,
		partnerCenter,
		affiliationCompany,
		userSelectRentValue,
		evBattery,
		getEvBatteryInfo,
	};
}
